/**
 * @lines98/client/services/oauth-callback — OAuth callback handler (T-6 client).
 *
 * Per ADR-0006 §D7 (server callback flow):
 *   1. Parse `?code=&state=` от current URL.
 *   2. Validate state matches sessionStorage (CSRF / replay protection).
 *   3. POST /auth/oauth/<provider>/callback с {code, state} + Bearer.
 *   4. On 200: server returns {status, playerId, secretToken, message}.
 *      Update identity (new secretToken; playerId may differ on subsequent-login).
 *   5. Clear sessionStorage; caller navigates back к app root.
 *
 * Pure-ish service (no DOM): screen layer (oauth-callback-screen) calls into
 * here and renders the result. Tests can drive `handleCallback()` directly.
 *
 * Cross-references:
 *   - ADR-0006 §D7 (callback flow + error responses).
 *   - ADR-0006 §D1 (callerPlayerId preserved at first_bind invariant).
 *   - services/identity.ts (update target).
 *   - services/oauth-login.ts (init sets sessionStorage).
 *   - screens/oauth-callback-screen.ts (consumer).
 */

import { t } from '../lib/i18n/index.js';

import { apiClient } from './api-client.js';
import * as identity from './identity.js';
import {
  clearOAuthState,
  readExpectedProvider,
  readExpectedState,
  type OAuthProvider,
} from './oauth-login.js';

// =============================================================================
// Types
// =============================================================================

/**
 * Server response shape for POST /auth/oauth/<provider>/callback. Aligned
 * with packages/server/src/routes/auth/oauth/shared.ts:callbackSuccessResponseSchema.
 */
interface OAuthCallbackResponse {
  playerId: string;
  secretToken: string;
  status: 'first_bind' | 'existing_binding';
  message: string;
}

interface ServerError {
  ok: false;
  kind: string;
  reason: string;
}

export type CallbackResult =
  | {
      readonly ok: true;
      readonly status: 'first_bind' | 'existing_binding';
      readonly playerId: string;
      readonly message: string;
    }
  | {
      readonly ok: false;
      readonly reason:
        | 'missing_query_params'
        | 'state_mismatch'
        | 'state_missing_in_session'
        | 'no_identity'
        | 'server_error';
      /** Human-readable detail (localized via lib/i18n `auth.oauth.*` — для display). */
      readonly detail: string;
      /** Server-side reason code if available (`token_exchange_failed`, etc.). */
      readonly serverReason?: string;
      readonly status?: number;
    };

// =============================================================================
// Query parsing
// =============================================================================

export interface ParsedCallbackQuery {
  readonly code: string | null;
  readonly state: string | null;
  /** OAuth providers signal user-cancel / error via these query params. */
  readonly errorReason: string | null;
}

/**
 * Parse the OAuth callback query string. Exported для testability (C-AC-10).
 *
 * Returns:
 *   - `code` / `state` from `?code=...&state=...` (happy path).
 *   - `errorReason` if provider returned `?error=...` (user cancelled, denied, etc.).
 */
export function parseCallbackQuery(searchString: string): ParsedCallbackQuery {
  const params = new URLSearchParams(searchString);
  return {
    code: params.get('code'),
    state: params.get('state'),
    errorReason: params.get('error'),
  };
}

// =============================================================================
// Provider extraction from URL path
// =============================================================================

/**
 * Extract OAuth provider from pathname. Pattern: `/oauth/<provider>/callback`.
 * Returns null if path doesn't match.
 */
export function extractProviderFromPath(pathname: string): OAuthProvider | null {
  const match = pathname.match(/^\/oauth\/([a-z]+)\/callback\/?$/);
  if (!match) return null;
  const candidate = match[1];
  if (candidate === 'google' || candidate === 'discord') return candidate;
  return null;
}

/**
 * Returns true if current location matches OAuth callback path pattern.
 * Called by main.ts on boot.
 */
export function isOAuthCallbackRoute(pathname: string): boolean {
  return extractProviderFromPath(pathname) !== null;
}

// =============================================================================
// Main flow
// =============================================================================

/**
 * Handle an OAuth callback. Pure flow function (no DOM); screen layer
 * renders the returned CallbackResult.
 *
 * Steps per ADR-0006 §D7 (from client perspective):
 *   1. Parse query → code + state.
 *   2. Validate state matches sessionStorage value (CSRF protection).
 *   3. POST /auth/oauth/<provider>/callback с {code, state}.
 *   4. On success: update identity store с new secretToken + playerId.
 *   5. Clear sessionStorage state regardless of outcome.
 */
export async function handleCallback(args: {
  provider: OAuthProvider;
  searchString: string;
}): Promise<CallbackResult> {
  const { provider, searchString } = args;
  const parsed = parseCallbackQuery(searchString);

  // Step 0: provider returned ?error= (user cancelled / denied consent).
  if (parsed.errorReason) {
    clearOAuthState();
    return {
      ok: false,
      reason: 'server_error',
      detail: t('auth.oauth.errProviderError', { reason: parsed.errorReason }),
      serverReason: parsed.errorReason,
    };
  }

  // Step 1: validate query shape.
  if (!parsed.code || !parsed.state) {
    return {
      ok: false,
      reason: 'missing_query_params',
      detail: t('auth.oauth.errMissingParams'),
    };
  }

  // Step 2: validate state matches sessionStorage (CSRF / tab-mixup defence).
  const expectedState = readExpectedState();
  const expectedProvider = readExpectedProvider();
  if (!expectedState) {
    return {
      ok: false,
      reason: 'state_missing_in_session',
      detail: t('auth.oauth.errStateMissing'),
    };
  }
  if (expectedState !== parsed.state) {
    clearOAuthState();
    return {
      ok: false,
      reason: 'state_mismatch',
      detail: t('auth.oauth.errStateMismatch'),
    };
  }
  if (expectedProvider && expectedProvider !== provider) {
    clearOAuthState();
    return {
      ok: false,
      reason: 'state_mismatch',
      detail: t('auth.oauth.errProviderMismatch', {
        expected: expectedProvider,
        actual: provider,
      }),
    };
  }

  // Step 3: ensure identity exists (Bearer required для callback endpoint).
  // Boot fallback in case sessionStorage was preserved across browser restart
  // (identity should also be loaded via main.ts boot path, but defensive).
  let current = identity.getCurrent();
  if (!current) {
    current = await identity.bootIdentity();
  }
  if (!current) {
    return {
      ok: false,
      reason: 'no_identity',
      detail: t('auth.oauth.errNoIdentity'),
    };
  }

  // Step 4: POST to server callback endpoint.
  const response = await apiClient.post<OAuthCallbackResponse>(
    `/auth/oauth/${provider}/callback`,
    { code: parsed.code, state: parsed.state },
  );

  // Always clear sessionStorage — state was consumed.
  clearOAuthState();

  if (!response.ok) {
    const errBody = response.body as ServerError | null;
    const serverReason =
      errBody && typeof errBody === 'object' && 'reason' in errBody
        ? String(errBody.reason)
        : 'unknown';
    return {
      ok: false,
      reason: 'server_error',
      detail: serverErrorMessage(response.status, serverReason),
      serverReason,
      status: response.status,
    };
  }

  // Step 5: update identity store (server may have changed playerId via
  // subsequent-login flow; secretToken always rotates). Persist authMethod so the
  // profile «Аккаунт» status line shows «Google»/«Discord» (spec §2.5). OAuth
  // accounts have no display_name in v1 → leave it for the profile fallback.
  await identity.update({
    playerId: response.body.playerId,
    secretToken: response.body.secretToken,
    authMethod: provider, // 'google' | 'discord'
  });

  return {
    ok: true,
    status: response.body.status,
    playerId: response.body.playerId,
    message: response.body.message,
  };
}

// =============================================================================
// Helpers
// =============================================================================

function serverErrorMessage(status: number, reason: string): string {
  if (status === 400) {
    return t('auth.oauth.errServer400', { reason });
  }
  if (status === 401) {
    return t('auth.oauth.errServer401', { reason });
  }
  if (status === 502) {
    return t('auth.oauth.errServer502', { reason });
  }
  if (status === 503) {
    return t('auth.oauth.errServer503', { reason });
  }
  return t('auth.oauth.errServerOther', { status, reason });
}
